import React, { useMemo } from "react";
import BaseChart from "../../components/ui/BaseChart";

const fullUsd = (n) =>
  `$${Number(n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// Join revenue & expense breakdowns by clientId → profit = revenue − expense.
const mergeProfit = (revenue = [], expense = []) => {
  const map = new Map();
  const keyOf = (e) => String(e.clientId ?? "__none__");
  (revenue ?? []).forEach((r) => {
    map.set(keyOf(r), { name: r.name, color: r.color, revenue: r.amountUsd, expense: 0 });
  });
  (expense ?? []).forEach((e) => {
    const k = keyOf(e);
    const cur = map.get(k) ?? { name: e.name, color: e.color, revenue: 0, expense: 0 };
    cur.expense = e.amountUsd;
    map.set(k, cur);
  });
  return [...map.values()].map((v) => ({
    ...v,
    profit: Math.round((v.revenue - v.expense) * 100) / 100,
  }));
};

const ProfitByCustomerChart = ({ revenueBreakdown, expenseBreakdown }) => {
  const { points, net, lossCount } = useMemo(() => {
    const merged = mergeProfit(revenueBreakdown, expenseBreakdown);
    const profitable = merged.filter((m) => m.profit > 0).sort((a, b) => b.profit - a.profit);
    const losses = merged.filter((m) => m.profit < 0);
    // Net = revenue − expenses across ALL customers (incl. losses) so the
    // headline reconciles with the Profit by Team net.
    const netProfit = merged.reduce((a, m) => a + m.profit, 0);
    return {
      points: profitable.map((m) => ({
        name: m.name,
        color: m.color,
        y: m.profit,
        revenue: m.revenue,
        expense: m.expense,
      })),
      net: Math.round(netProfit * 100) / 100,
      lossCount: losses.length,
    };
  }, [revenueBreakdown, expenseBreakdown]);

  const chartOptions = useMemo(() => {
    if (!points.length) return null;
    return {
      chart: {
        type: "pie",
        height: 340,
        backgroundColor: "transparent",
        style: { fontFamily: "Poppins, sans-serif" },
      },
      title: { text: null },
      tooltip: {
        backgroundColor: "#1e293b",
        style: { color: "#f8fafc", fontSize: "11px" },
        borderWidth: 0,
        borderRadius: 6,
        useHTML: true,
        pointFormatter() {
          return `<span style="color:${this.color}">●</span> <b>${this.name}</b><br/>Profit ${fullUsd(this.y)} · <b>${this.percentage.toFixed(1)}%</b><br/><span style="opacity:.7">Rev ${fullUsd(this.revenue)} · Exp ${fullUsd(this.expense)}</span>`;
        },
      },
      plotOptions: {
        pie: {
          innerSize: "62%",
          borderWidth: 2,
          borderColor: "#ffffff",
          dataLabels: { enabled: false },
          showInLegend: true,
          states: { hover: { halo: { size: 6 } } },
        },
      },
      legend: {
        layout: "vertical",
        align: "right",
        verticalAlign: "middle",
        maxHeight: 280,
        navigation: { activeColor: "#4F46E5", inactiveColor: "#cbd5e1" },
        itemStyle: { fontSize: "10px", fontWeight: "normal", color: "#555" },
        itemHoverStyle: { color: "#111" },
        symbolRadius: 6,
        symbolHeight: 8,
        symbolWidth: 8,
      },
      series: [{ name: "Profit", data: points }],
    };
  }, [points]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="text-sm font-semibold text-gray-700">Profit by Customer</h4>
          <p className="text-[11px] text-gray-400 mt-0.5">Revenue − Expenses · USD</p>
        </div>
        {points.length > 0 && (
          <span className="text-[11px] text-gray-400">
            Net{" "}
            <span className={`font-semibold ${net >= 0 ? "text-emerald-600" : "text-red-500"}`}>
              {fullUsd(net)}
            </span>
          </span>
        )}
      </div>

      {chartOptions ? (
        <>
          <BaseChart options={chartOptions} className="w-full" />
          {lossCount > 0 && (
            <p className="text-[10px] text-gray-400 mt-2">
              <i className="fa-solid fa-circle-info mr-1" />
              {lossCount} {lossCount === 1 ? "customer is" : "customers are"} loss-making (negative
              profit) and not shown in the pie.
            </p>
          )}
        </>
      ) : (
        <p className="text-sm text-gray-400 py-20 text-center">No profit data for this period.</p>
      )}
    </div>
  );
};

export default ProfitByCustomerChart;
